import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import LanguageToggle from "@/components/LanguageToggle";
import Logo from "@/components/Logo";
import { ArrowLeft, Send, Loader2, Mail, Phone, Building2, Hash } from "lucide-react";
import { toast } from "sonner";

// CHEPL corporate details — the entity that operates JobGrid
export const CHEPL = {
  name: "CHEPL bv",
  email: "info@jobgrid.nl",
  phone: "030 207 2501",
  kvk: "42087917",
  btw: "NL869653817B01",
};

export default function Contact() {
  const { t } = useT();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", { name: name.trim(), email: email.trim(), subject: subject.trim(), message: message.trim() });
      toast.success(t("contactSent"));
      setSent(true);
      setName(""); setEmail(""); setSubject(""); setMessage("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("contactFailed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ed-page min-h-screen">
      <header className="ed-header px-8 py-5 flex items-center justify-between">
        <Logo size="md" showTagline={false} className="mr-3" data-testid="contact-logo" />
        <div className="flex items-center gap-4">
          <Link
            to="/partner"
            data-testid="contact-nav-partner"
            className="font-mono text-xs uppercase tracking-[0.15em] text-neutral-600 hover:text-neutral-900 transition-colors hidden sm:inline"
          >
            {t("landNavPartner")}
          </Link>
          <LanguageToggle testId="contact-language-toggle" />
          <Link to="/" data-testid="contact-back" className="ed-btn ed-btn-outline">
            <ArrowLeft size={14} strokeWidth={1.5} /> <span className="hidden sm:inline">{t("contactBack")}</span>
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-8 py-16 lg:py-24 grid lg:grid-cols-5 gap-12">
        <div className="lg:col-span-3">
          <p className="ed-eyebrow" data-testid="contact-eyebrow">{t("contactEyebrow")}</p>
          <h1 className="ed-h1 mt-4">{t("contactTitle")}</h1>
          <p className="mt-6 text-neutral-700 leading-relaxed max-w-lg">{t("contactSubtitle")}</p>

          {sent ? (
            <div className="mt-10 border border-emerald-500/40 bg-emerald-50 p-6" data-testid="contact-success">
              <p className="font-serif text-2xl">{t("contactSuccessTitle")}</p>
              <p className="text-sm text-neutral-700 mt-2 leading-relaxed">{t("contactSuccessBody")}</p>
              <button
                onClick={() => setSent(false)}
                className="mt-4 font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-700 underline underline-offset-2"
                data-testid="contact-send-another"
              >
                {t("contactSendAnother")}
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-10 space-y-4" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="ed-label mb-1 block">{t("contactName")}</span>
                  <input data-testid="contact-name" className="ed-input" required minLength={2}
                         value={name} onChange={(e) => setName(e.target.value)} />
                </label>
                <label className="block">
                  <span className="ed-label mb-1 block">{t("contactEmail")}</span>
                  <input data-testid="contact-email" type="email" className="ed-input" required
                         value={email} onChange={(e) => setEmail(e.target.value)} />
                </label>
              </div>
              <label className="block">
                <span className="ed-label mb-1 block">{t("contactSubject")}</span>
                <input data-testid="contact-subject" className="ed-input" required minLength={3}
                       value={subject} onChange={(e) => setSubject(e.target.value)} />
              </label>
              <label className="block">
                <span className="ed-label mb-1 block">{t("contactMessage")}</span>
                <textarea data-testid="contact-message" className="ed-input min-h-[10rem]" required minLength={10} maxLength={4000}
                          value={message} onChange={(e) => setMessage(e.target.value)} />
                <span className="text-[0.6rem] font-mono text-neutral-500">{message.length}/4000</span>
              </label>
              <button data-testid="contact-submit" type="submit" disabled={busy}
                      className="inline-flex items-center gap-2 bg-[#24211D] text-[#F9F8F6] px-6 py-3.5 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#3D3934] transition-colors disabled:opacity-60">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} strokeWidth={1.5} />}
                {t("contactSubmit")}
              </button>
            </form>
          )}
        </div>

        <aside className="lg:col-span-2" data-testid="contact-details">
          <div className="border border-black/10 bg-white p-8">
            <p className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-neutral-500">{t("contactDetailsEyebrow")}</p>
            <p className="font-serif text-2xl mt-2">{CHEPL.name}</p>
            <ul className="mt-6 space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <Mail size={13} strokeWidth={1.5} className="mt-1 shrink-0 text-neutral-500" />
                <a href={`mailto:${CHEPL.email}`} className="hover:underline underline-offset-2">{CHEPL.email}</a>
              </li>
              <li className="flex items-start gap-3">
                <Phone size={13} strokeWidth={1.5} className="mt-1 shrink-0 text-neutral-500" />
                <a href={`tel:+31${CHEPL.phone.replace(/\D/g, "").slice(1)}`} className="hover:underline underline-offset-2">{CHEPL.phone}</a>
              </li>
              <li className="flex items-start gap-3">
                <Building2 size={13} strokeWidth={1.5} className="mt-1 shrink-0 text-neutral-500" />
                <span className="font-mono text-xs">KvK {CHEPL.kvk}</span>
              </li>
              <li className="flex items-start gap-3">
                <Hash size={13} strokeWidth={1.5} className="mt-1 shrink-0 text-neutral-500" />
                <span className="font-mono text-xs">BTW {CHEPL.btw}</span>
              </li>
            </ul>
          </div>
        </aside>
      </main>
    </div>
  );
}
